import { useMemo, useState, useEffect } from 'react';
import { ChevronDown, ChevronUp, Sparkles, Plus } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Masonry from 'react-masonry-css';
import { useTranslation } from 'react-i18next';
import { usePlans } from '../contexts/PlanContext';
import { useCategories } from '../contexts/CategoryContext';
import { useTransaction } from '../contexts/TransactionContext';
import { useUserPreferences } from '../contexts/UserPreferencesContext';
import { BudgetStatus } from '../components/BudgetStatus';
import Header from '../components/Header';
import TypewriterText from '../components/TypewriterText';
import { SubCategoryModal, SubCategoryFormData } from '../components/SubCategoryModal';
import { PixelHourglass } from '../components/PixelHourglass';

export default function HomePage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { currentPlan, currentMonthPlan, allMonthPlans } = usePlans();
  const { categories, budgetStatus, loading, createCategory } = useCategories();
  const { transactions, fetchTransactions, loading: transactionsLoading } = useTransaction();
  const { formatMoney } = useUserPreferences();
  const [isPengeluaranExpanded, setIsPengeluaranExpanded] = useState(false);
  const [isKomposisiExpanded, setIsKomposisiExpanded] = useState(false);
  const [isSpendingExpanded, setIsSpendingExpanded] = useState(false);
  const [isGoalsExpanded, setIsGoalsExpanded] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [showAddGoalModal, setShowAddGoalModal] = useState(false);

  // Spending Summary state
  const [selectedCategoryValue, setSelectedCategoryValue] = useState<string>('all'); // Format: 'all' | 'parent:id' | 'child:id'

  // Fetch transactions when spending summary is expanded
  useEffect(() => {
    if (isSpendingExpanded && currentMonthPlan) {
      // Fetch current month transactions
      fetchTransactions(currentMonthPlan.id);
    }
  }, [isSpendingExpanded, currentMonthPlan?.id]);

  // Calculate totals
  const totals = useMemo(() => {
    let totalAllocated = 0;
    let totalSpending = 0;
    let totalRemaining = 0;

    categories.forEach((group) => {
      group.children.forEach((child) => {
        totalAllocated += child.allocated || 0;
        totalSpending += child.transaction || 0;
        totalRemaining += child.balance || 0;
      });
    });

    return { totalAllocated, totalSpending, totalRemaining };
  }, [categories]);

  // Calculate percentages for the composite bar
  const percentages = useMemo(() => {
    if (totals.totalAllocated === 0) {
      return { spending: 0, remaining: 0 };
    }

    return {
      spending: (totals.totalSpending / totals.totalAllocated) * 100,
      remaining: (totals.totalRemaining / totals.totalAllocated) * 100,
    };
  }, [totals]);

  // Calculate budget composition by groups (only when section is expanded)
  const budgetComposition = useMemo(() => {
    // Return empty data if section is collapsed
    if (!isKomposisiExpanded) {
      return { top5Groups: [], barChartData: [] };
    }

    // Only 5 distinct colors for top 5 groups
    const top5Colors = [
      '#ff6b6b', // Red
      '#4ecdc4', // Teal
      '#f9ca24', // Yellow
      '#6c5ce7', // Purple
      '#fd79a8', // Pink
    ];

    // Get all groups with allocation
    const allGroups = categories
      .filter((group) => !group.is_installment) // Filter out installment groups
      .map((group) => {
        const groupAllocated = group.children.reduce(
          (sum, child) => sum + (child.allocated || 0),
          0
        );
        const percentage = totals.totalAllocated > 0
          ? (groupAllocated / totals.totalAllocated) * 100
          : 0;

        return {
          id: group.id,
          name: group.name,
          allocated: groupAllocated,
          percentage,
          children: group.children.map((child) => ({
            id: child.id,
            name: child.name,
            allocated: child.allocated || 0,
            percentageOfGroup: groupAllocated > 0
              ? ((child.allocated || 0) / groupAllocated) * 100
              : 0,
          })),
        };
      })
      .filter((group) => group.allocated > 0) // Only show groups with allocation
      .sort((a, b) => b.percentage - a.percentage); // Sort from highest to lowest

    // Assign colors: top 5 get distinct colors, rest combined as "Others"
    const top5Groups = allGroups.slice(0, 5).map((group, index) => ({
      ...group,
      color: top5Colors[index],
    }));

    // Combine remaining groups into "Others"
    const remainingGroups = allGroups.slice(5);
    const othersData = remainingGroups.length > 0 ? {
      id: 'others',
      name: 'Lainnya',
      allocated: remainingGroups.reduce((sum, g) => sum + g.allocated, 0),
      percentage: remainingGroups.reduce((sum, g) => sum + g.percentage, 0),
      color: '#9e9e9e', // Grey
      children: [],
    } : null;

    // For bar chart display (highest to lowest from left to right)
    const barChartData = othersData
      ? [...top5Groups, othersData]
      : top5Groups;

    return { top5Groups, barChartData };
  }, [isKomposisiExpanded, categories, totals.totalAllocated]);

  // Toggle group expansion
  const toggleGroup = (groupId: string) => {
    setExpandedGroups((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(groupId)) {
        newSet.delete(groupId);
      } else {
        newSet.add(groupId);
      }
      return newSet;
    });
  };

  // Category options for hierarchical dropdown (same as HistoryPage)
  const categoryOptions = useMemo(() => {
    const options: Array<{ value: string; label: string; type: 'parent' | 'child'; isParent?: boolean }> = [];

    categories.forEach((group) => {
      // Skip installment groups
      if (group.is_installment) return;

      // Add parent category (group)
      options.push({
        value: `parent:${group.id}`,
        label: group.name,
        type: 'parent',
        isParent: true
      });

      // Add subcategories
      group.children.forEach((child) => {
        options.push({
          value: `child:${child.id}`,
          label: `${group.name} - ${child.name}`, // Format: "Parent - Child"
          type: 'child'
        });
      });
    });

    return options;
  }, [categories]);

  // Calculate spending summary data (only when section is expanded)
  const spendingSummary = useMemo(() => {
    if (!isSpendingExpanded || !currentMonthPlan || !transactions) {
      return { dailyData: [], allDates: [], stats: null };
    }

    try {
      // Extract YYYY-MM from month (could be YYYY-MM or YYYY-MM-DD)
      const monthString = currentMonthPlan.month.slice(0, 7); // Get YYYY-MM
      const currentMonthDate = new Date(monthString + '-01');

      if (isNaN(currentMonthDate.getTime())) {
        console.error('Invalid month format:', currentMonthPlan.month);
        return { dailyData: [], allDates: [], stats: null };
      }

      const lastMonthDate = new Date(currentMonthDate);
      lastMonthDate.setMonth(lastMonthDate.getMonth() - 1);
      const lastMonthString = lastMonthDate.toISOString().slice(0, 7);
      const lastMonthPlan = allMonthPlans?.find(mp => mp.month.startsWith(lastMonthString));

      // Filter transactions by month_plan_id for the chart
      // Include both expense and transfer transactions (transfers have transfer_pair_id)
      const monthPlanTransactions = transactions.filter(t =>
        t.month_plan_id === currentMonthPlan.id && (t.transfer_pair_id || t.transaction_type === 'expense')
      );
      console.log('Transactions for this month plan:', monthPlanTransactions.length);
      console.log('Sample transactions:', monthPlanTransactions.slice(0, 3).map(t => ({
        id: t.id,
        type: t.transaction_type,
        transfer_pair_id: t.transfer_pair_id,
        category_id: t.category_id,
        amount: t.amount
      })));

      // Parse selected category value
      let selectedType: 'all' | 'parent' | 'child' = 'all';
      let selectedId: string | null = null;

      if (selectedCategoryValue !== 'all') {
        const [type, id] = selectedCategoryValue.split(':');
        selectedType = type as 'parent' | 'child';
        selectedId = id;
      }

      // Filter by group/category for both chart AND stats
      let filteredTransactions = monthPlanTransactions;
      let relevantCategories = categories.filter(g => !g.is_installment).flatMap(g => g.children);

      if (selectedType === 'child') {
        // Specific category selected
        filteredTransactions = monthPlanTransactions.filter(t => t.category_id === selectedId);
        relevantCategories = relevantCategories.filter(c => c.id === selectedId);
      } else if (selectedType === 'parent') {
        // Specific group selected - filter transactions and categories
        const group = categories.find(g => g.id === selectedId);
        const categoryIds = group?.children.map(c => c.id) || [];
        filteredTransactions = monthPlanTransactions.filter(t => t.category_id && categoryIds.includes(t.category_id));
        relevantCategories = group?.children || [];
      }

      console.log('Filtered transactions:', filteredTransactions.length);
      console.log('Relevant categories:', relevantCategories.length);
      console.log('Sample filtered transactions:', filteredTransactions.slice(0, 3).map(t => ({
        id: t.id,
        category_id: t.category_id,
        transfer_pair_id: t.transfer_pair_id,
        amount: t.amount
      })));

      // Calculate total spent using category.transaction (same as Pengeluaran section)
      const totalSpentFromCategories = relevantCategories.reduce((sum, cat) => sum + (cat.transaction || 0), 0);
      console.log('Total spent (from categories):', totalSpentFromCategories);

      // Calculate total allocated
      const totalAllocated = relevantCategories.reduce((sum, cat) => sum + (cat.allocated || 0), 0);
      console.log('Total allocated:', totalAllocated);
      console.log('==============================');

      // Group transactions by date and sort chronologically
      // NOTE: Transaction date is used ONLY for grouping/sorting, NOT for filtering
      // Filtering is done by month_plan_id above
      // IMPORTANT: Use ONLY filteredTransactions dates (for selected category/group)
      const dailyTotals: { [key: string]: number } = {};
      filteredTransactions.forEach(t => {
        const date = t.date.slice(0, 10); // YYYY-MM-DD
        dailyTotals[date] = (dailyTotals[date] || 0) + Math.abs(t.amount);
      });

      // Get all unique dates from FILTERED transactions only and sort them
      // This ensures chart starts from oldest transaction in selected category
      const allDates = Object.keys(dailyTotals).sort();

      // Calculate cumulative spending for each date
      const dailyData: { date: string; amount: number }[] = [];
      let cumulative = 0;
      allDates.forEach(date => {
        cumulative += dailyTotals[date];
        dailyData.push({ date, amount: cumulative });
      });

      // Get total days (from first to last transaction)
      const totalDays = allDates.length;

      // Use totals calculated from categories (matches Pengeluaran section)
      const currentPercent = totalAllocated > 0 ? (totalSpentFromCategories / totalAllocated) * 100 : 0;

      // Calculate last month stats (same day range)
      let lastMonthPercent = 0;
      if (lastMonthPlan) {
        // This would need last month's transactions - simplified for now
        lastMonthPercent = 0; // Placeholder
      }

      const growth = lastMonthPercent > 0
        ? ((currentPercent - lastMonthPercent) / lastMonthPercent) * 100
        : 0;

      return {
        dailyData,
        allDates,
        stats: {
          totalDays,
          currentPercent: currentPercent.toFixed(1),
          lastMonthPercent: lastMonthPercent.toFixed(1),
          growth: growth.toFixed(1),
          totalSpent: totalSpentFromCategories,
          totalAllocated,
        },
      };
    } catch (error) {
      console.error('Error calculating spending summary:', error);
      return { dailyData: [], allDates: [], stats: null };
    }
  }, [isSpendingExpanded, transactions, categories, selectedCategoryValue, currentMonthPlan, allMonthPlans]);

  // Calculate goals data from categories with is_goal flag (only when expanded for performance)
  const goalsData = useMemo(() => {
    // Only calculate when Goals section is expanded (performance optimization)
    if (!isGoalsExpanded) {
      return [];
    }

    // Flatten all children from all parent categories
    const allCategories = categories.flatMap(group => group.children || []);
    console.log('Total child categories:', allCategories.length);
    console.log('Categories with is_goal:', allCategories.filter(cat => cat.is_goal).length);
    console.log('Sample child categories:', allCategories.slice(0, 3).map(cat => ({
      id: cat.id,
      name: cat.name,
      is_goal: cat.is_goal,
      goal_target: cat.goal_target,
      goal_saved: cat.goal_saved
    })));

    // Check both parent categories AND their children
    const goals = allCategories
      .filter(cat => cat.is_goal && cat.goal_target && cat.goal_target > 0)
      .map(cat => {
        const target = cat.goal_target as number;
        const progress = target > 0 ? (cat.goal_saved / target) * 100 : 0;
        return {
          id: cat.id,
          name: cat.name,
          targetAmount: cat.goal_target,
          currentAmount: cat.goal_saved,
          progress: Math.min(progress, 100), // Cap at 100%
          targetMonth: cat.goal_target_month,
          monthlyTarget: cat.goal_monthly_target,
        };
      })
      .sort((a, b) => b.progress - a.progress); // Sort by progress (highest first)

    console.log('Filtered goals:', goals.length);
    console.log('Goals data:', goals);
    console.log('==================');
    return goals;
  }, [isGoalsExpanded, categories]);

  // Parent categories for goal creation dropdown
  const parentCategories = useMemo(() => {
    return categories
      .filter(cat => !cat.is_installment) // Exclude installment groups
      .map(cat => ({
        id: cat.id,
        name: cat.name
      }));
  }, [categories]);

  // Handle saving new goal category
  const handleSaveGoalCategory = async (data: SubCategoryFormData & { parent_category_id?: string }) => {
    if (!currentPlan || !currentMonthPlan || !data.parent_category_id) {
      throw new Error('Plan atau grup tidak ditemukan');
    }

    // Create category using the same method as CategoryList
    await createCategory({
      name: data.name,
      plan_id: currentPlan.id,
      current_month_plan_id: currentMonthPlan.id,
      parent_id: data.parent_category_id,
      is_installment: data.is_installment,
      initial_installment_term: data.installment_term,
      initial_installment_progress: data.installment_progress,
      initial_allocated: data.allocated,
      initial_same_allocation: data.same_allocation,
      initial_is_goal: data.is_goal,
      initial_goal_target: data.goal_target,
      initial_goal_target_month: data.goal_target_month,
      initial_goal_monthly_plan: data.goal_monthly_plan,
    });

    // Close modal after successful creation
    setShowAddGoalModal(false);
  };

  return (
    <div
      className="min-h-screen bg-[#ffffeb] pb-24 lg:pb-8"
      style={{
        backgroundImage: `
          repeating-linear-gradient(0deg, transparent, transparent 7px, rgba(7, 127, 93, 0.03) 7px, rgba(7, 127, 93, 0.03) 8px),
          repeating-linear-gradient(90deg, transparent, transparent 7px, rgba(7, 127, 93, 0.03) 7px, rgba(7, 127, 93, 0.03) 8px)
        `
      }}
    >
      {/* Header with Plan Name */}
      <Header
        leftContent={
          <p
            className="text-left text-base font-bold uppercase hover:opacity-80 inline-block"
            style={{
              fontFamily: "'Press Start 2P', monospace",
              transition: 'none',
              fontSize: '14px',
            }}
          >
            {currentPlan?.name || t('home.plan_fallback')}
          </p>
        }
        maxWidth="max-w-2xl"
      />

      {/* Main Content */}
      <div className="px-4 py-4 max-w-7xl mx-auto">
        {/* Money to Assign - Always full width, outside Masonry */}
        {loading ? (
          <div
            className="p-4 border-4 border-[#077f5d] bg-[#a9edba] flex items-center justify-center mb-4"
            style={{ boxShadow: '4px 4px 0 #077f5d' }}
            role="status"
            aria-live="polite"
          >
            <PixelHourglass size="small" speed="fast" color="keppel" data-id="budget-status-loader" />
            <span className="sr-only">Loading budget status...</span>
          </div>
        ) : (
          budgetStatus && (
            <div className="mb-4">
              <BudgetStatus
                status={budgetStatus.status}
                unallocated_amount={budgetStatus.unallocated_amount}
                underfunded_amount={budgetStatus.underfunded_amount}
              />
            </div>
          )
        )}

        {/* Masonry Layout for Cards */}
        <Masonry
          breakpointCols={{
            default: 3,
            1280: 2,
            768: 1
          }}
          className="masonry-grid"
          columnClassName="masonry-grid-column"
        >

          {/* Pengeluaran Card - Collapsible */}
          <div className="bg-white border-2 border-[#077f5d]" style={{ boxShadow: '4px 4px 0 #077f5d' }}>
            {/* Card Header - Collapsible */}
            <button
              onClick={() => setIsPengeluaranExpanded(!isPengeluaranExpanded)}
              className="w-full flex items-center justify-start gap-3 p-4 bg-[#a9edba] border-b-2 border-[#077f5d]"
            >
              {isPengeluaranExpanded ? (
                <ChevronUp className="w-5 h-5 text-[#077f5d]" />
              ) : (
                <ChevronDown className="w-5 h-5 text-[#077f5d]" />
              )}
              <h2
                className="text-lg text-[#1a1a1a] uppercase"
                style={{ fontFamily: "'Press Start 2P', monospace", lineHeight: '1.4', fontSize: '12px' }}
              >
                {t('home.expenses.title')}
              </h2>
            </button>

            {/* Card Content */}
            {isPengeluaranExpanded && (
              <div className="p-4">
                {totals.totalAllocated === 0 ? (
                  /* Empty State */
                  <div className="text-center py-8">
                    <p
                      className="text-[#077f5d] font-bold mb-4 text-sm"
                      style={{ fontFamily: "'Press Start 2P', monospace", lineHeight: '1.6' }}
                    >
                      {t('home.expenses.no_allocation_title')}
                    </p>
                    <TypewriterText
                      texts={[
                        t('home.expenses.no_allocation_text_1'),
                        `${t('home.expenses.no_allocation_text_2')}`
                      ]}
                      speed={35}
                      delayBetween={500}
                      showCursor={true}
                      className="text-gray-600 text-sm font-mono mb-6 px-4"
                      onFinish={() => console.log('Typing done')}
                      startOnView={false}
                    />
                    <div className="flex justify-center">
                      <button
                        onClick={() => navigate('/budget')}
                        className="px-6 py-3 bg-[#3cbe8c] text-white border-2 border-[#077f5d] font-bold uppercase"
                        style={{
                          fontFamily: "'Press Start 2P', monospace",
                          fontSize: '10px',
                          boxShadow: '4px 4px 0 #077f5d',
                          transition: 'all 0.1s'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.boxShadow = '2px 2px 0 #077f5d';
                          e.currentTarget.style.transform = 'translate(-2px, -2px)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.boxShadow = '4px 4px 0 #077f5d';
                          e.currentTarget.style.transform = 'translate(0, 0)';
                        }}
                      >
                        {t('home.expenses.set_budget')}
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    {/* Bar Chart */}
                    <div className="mb-4">
                      <div className="flex h-6 border-2 border-[#077f5d] overflow-hidden">
                        {/* Spending */}
                        {percentages.spending > 0 && (
                          <div
                            className="bg-[#ff6b6b] flex items-center justify-center text-white font-bold font-mono text-xs"
                            style={{ width: `${percentages.spending}%` }}
                            title={`${t('home.expenses.spending')}: ${percentages.spending.toFixed(1)}%`}
                          >
                            {percentages.spending >= 10 && `${percentages.spending.toFixed(0)}%`}
                          </div>
                        )}

                        {/* Remaining */}
                        {percentages.remaining > 0 && (
                          <div
                            className="bg-[#3cbe8c] flex items-center justify-center text-white font-bold font-mono text-xs"
                            style={{ width: `${percentages.remaining}%` }}
                            title={`${t('home.expenses.remaining')}: ${percentages.remaining.toFixed(1)}%`}
                          >
                            {percentages.remaining >= 10 && `${percentages.remaining.toFixed(0)}%`}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Simple List - No Boxes */}
                    <div className="space-y-2 font-mono">
                      {/* Pengeluaran */}
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 bg-[#ff6b6b] border-2 border-[#077f5d]"></div>
                          <span className="text-sm text-[#1a1a1a]">{t('home.expenses.spending')}</span>
                        </div>
                        <span className="text-sm font-bold text-[#077f5d]">
                          {formatMoney(totals.totalSpending)}
                        </span>
                      </div>

                      {/* Sisa */}
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 bg-[#3cbe8c] border-2 border-[#077f5d]"></div>
                          <span className="text-sm text-[#1a1a1a]">{t('home.expenses.remaining')}</span>
                        </div>
                        <span className="text-sm font-bold text-[#077f5d]">
                          {formatMoney(totals.totalRemaining)}
                        </span>
                      </div>

                      {/* Total with line separator */}
                      <div className="flex items-center justify-between border-t-2 border-[#077f5d] mt-2 pt-3">
                        <span className="text-sm font-bold text-[#077f5d] uppercase">
                          {t('home.expenses.total')}
                        </span>
                        <span className="text-sm font-bold text-[#077f5d]">
                          {formatMoney(totals.totalAllocated)}
                        </span>
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>

          {/* 7. Goals Snapshot Card - Collapsible */}
          <div className="bg-white border-2 border-[#077f5d]" style={{ boxShadow: '4px 4px 0 #077f5d' }}>
            {/* Card Header - Collapsible */}
            <button
              onClick={() => setIsGoalsExpanded(!isGoalsExpanded)}
              className="w-full flex items-center justify-start gap-3 p-4 bg-[#a9edba] border-b-2 border-[#077f5d]"
            >
              {isGoalsExpanded ? (
                <ChevronUp className="w-5 h-5 text-[#077f5d]" />
              ) : (
                <ChevronDown className="w-5 h-5 text-[#077f5d]" />
              )}
              <h2
                className="text-lg text-[#1a1a1a] uppercase"
                style={{ fontFamily: "'Press Start 2P', monospace", lineHeight: '1.4', fontSize: '12px' }}
              >
                {t('home.goals.title')}
              </h2>
            </button>

            {/* Card Content */}
            {isGoalsExpanded && (
              <div className="p-4">
                {goalsData.length > 0 ? (
                  <div className="space-y-3">
                    {goalsData.map((goal) => {
                      const isCompleted = goal.progress >= 100;
                      const isAlmostThere = goal.progress >= 75 && goal.progress < 100;
                      const isHalfway = goal.progress >= 50 && goal.progress < 75;

                      // Get motivational message based on progress
                      const getMotivationalMessage = () => {
                        if (isCompleted) return t('home.goals.achieved');
                        if (isAlmostThere) return t('home.goals.almost_there');
                        if (isHalfway) return t('home.goals.halfway');
                        return t('home.goals.keep_saving');
                      };

                      return (
                        <div
                          key={goal.id}
                          className={`border-2 p-3 relative overflow-hidden border-[#077f5d] bg-[#ffffeb]`}
                          style={{
                            boxShadow: '2px 2px 0 #077f5d'
                          }}
                        >
                          {/* Celebration Banner for Completed Goals */}
                          {isCompleted && (
                            <div className="absolute top-0 right-0 bg-[#ffd700] border-l-2 border-b-2 border-[#ffd700] px-3 py-1 flex items-center gap-1">
                              <p className="text-sm font-bold font-mono text-white">
                                {goal.progress.toFixed(0)}%
                              </p>
                            </div>
                          )}

                          {/* Sparkles for almost complete goals */}
                          {isAlmostThere && (
                            <div className="absolute top-2 right-2">
                              <Sparkles className="w-4 h-4 text-[#ffd700] animate-pulse" />
                            </div>
                          )}

                          {/* Goal name and progress */}
                          <div className="flex items-center justify-between mb-2">
                            <p className="text-sm font-bold font-mono flex items-center gap-2 text-[#077f5d]">
                              {goal.name}
                            </p>
                            <p className={`text-sm font-bold font-mono ${isCompleted ? 'text-[#ffd700]' : 'text-[#077f5d]'
                              }`}>
                              {goal.progress.toFixed(0)}%
                            </p>
                          </div>

                          {/* Progress bar with animated fill */}
                          <div
                            className={`w-full h-6 border-2 bg-white mb-2 relative overflow-hidden ${isCompleted ? 'border-[#ffd700]' : 'border-[#077f5d]'
                              }`}
                            style={{
                              boxShadow: `${isCompleted ? '2px 2px 0 #ffd700' : '2px 2px 0 #077f5d'
                                }`
                            }}
                          >
                            <div
                              className={`h-full transition-all duration-500 relative ${isCompleted
                                ? 'bg-gradient-to-r from-[#ffd700] via-[#ffed4e] to-[#ffd700]'
                                : isAlmostThere
                                  ? 'bg-gradient-to-r from-[#3cbe8c] to-[#ffd700]'
                                  : 'bg-[#3cbe8c]'
                                }`}
                              style={{
                                width: `${Math.min(goal.progress, 100)}%`,
                                backgroundSize: isCompleted ? '200% 100%' : '100% 100%',
                                animation: isCompleted ? 'shimmer 2s linear infinite' : 'none'
                              }}
                            >
                              {/* Shine effect */}
                              {isCompleted && (
                                <div
                                  className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-30"
                                  style={{
                                    animation: 'slide 2s ease-in-out infinite'
                                  }}
                                />
                              )}
                            </div>
                          </div>

                          {/* Motivational Message */}
                          <div className={`mb-2 px-2 py-1 text-center text-xs font-mono font-bold ${isCompleted
                            ? 'bg-[#ffd700] border-2 border-[#ffd700]'
                            : isAlmostThere
                              ? 'bg-[#a9edba]'
                              : isHalfway
                                ? 'bg-[#e0f2fe]'
                                : 'bg-[#f0f9ff]'
                            }`}
                          >
                            <TypewriterText
                              texts={[getMotivationalMessage()]}
                              speed={60}
                              delayBetween={500}
                              showCursor={false}
                              className={`font-mono text-center ${isCompleted
                                ? 'text-white'
                                : isAlmostThere
                                  ? 'text-[#077f5d]'
                                  : isHalfway
                                    ? 'text-[#0369a1]'
                                    : 'text-gray-600'
                                }`}
                              onFinish={() => console.log('Typing done')}
                              startOnView={true}
                            />
                          </div>

                          {/* Amount details */}
                          <div className="flex items-center justify-end text-[10px] font-mono text-gray-600">
                            <span>{formatMoney(goal.currentAmount)} / {formatMoney(goal.targetAmount || 0)}</span>
                          </div>

                          {/* Target month if available */}
                          {goal.targetMonth && (
                            <div className="mt-1 text-xs font-mono text-gray-500 flex items-center justify-end text-[10px]">
                              {new Date(goal.targetMonth + '-01').toLocaleDateString('id-ID', { month: 'long', year: 'numeric' })}
                            </div>
                          )}
                        </div>
                      );
                    })}

                    {/* Add Goal Button - After goals list */}
                    <button
                      onClick={() => setShowAddGoalModal(true)}
                      className="w-full mt-3 px-4 py-3 flex items-center justify-center gap-2 bg-white text-[#077f5d] border-2 border-[#077f5d] font-bold font-mono text-sm uppercase hover:bg-[#dffbd0]"
                      style={{
                        boxShadow: '4px 4px 0 #077f5d',
                        transition: 'all 0.1s'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.boxShadow = '2px 2px 0 #077f5d';
                        e.currentTarget.style.transform = 'translate(-2px, -2px)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.boxShadow = '4px 4px 0 #077f5d';
                        e.currentTarget.style.transform = 'translate(0, 0)';
                      }}
                    >
                      <Plus size={16} />
                      {t('home.goals.add_category')}
                    </button>
                  </div>
                ) : (
                  /* Empty State */
                  <div className="text-center py-8">
                    <p
                      className="text-[#077f5d] font-bold mb-4 text-sm"
                      style={{ fontFamily: "'Press Start 2P', monospace", lineHeight: '1.6' }}
                    >
                      {t('home.goals.empty_title')}
                    </p>
                    <TypewriterText
                      texts={[
                        t('home.goals.empty_text')
                      ]}
                      speed={35}
                      delayBetween={500}
                      showCursor={true}
                      className="text-gray-600 text-sm font-mono mb-6 px-4"
                      onFinish={() => console.log('Typing done')}
                      startOnView={false}
                    />
                    <div className="flex justify-center">
                      <button
                        onClick={() => setShowAddGoalModal(true)}
                        className="px-6 py-3 flex items-center p-2 bg-[#3abf8c] text-white border-2 border-[#077f5d] font-bold uppercase"
                        style={{
                          fontFamily: "'Press Start 2P', monospace",
                          fontSize: '10px',
                          boxShadow: '4px 4px 0 #077f5d',
                          transition: 'none'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.boxShadow = '2px 2px 0 #077f5d';
                          e.currentTarget.style.transform = 'translate(-2px, -2px)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.boxShadow = '4px 4px 0 #077f5d';
                          e.currentTarget.style.transform = 'translate(0, 0)';
                        }}
                      >
                        <Plus className="pr-2" size={24} />
                        {t('home.goals.add_category')}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Budget Composition Card - Collapsible */}
          <div className="bg-white border-2 border-[#077f5d]" style={{ boxShadow: '4px 4px 0 #077f5d' }}>
            {/* Card Header - Collapsible */}
            <button
              onClick={() => setIsKomposisiExpanded(!isKomposisiExpanded)}
              className="w-full flex items-center justify-start gap-3 p-4 bg-[#a9edba] border-b-2 border-[#077f5d]"
            >
              {isKomposisiExpanded ? (
                <ChevronUp className="w-5 h-5 text-[#077f5d]" />
              ) : (
                <ChevronDown className="w-5 h-5 text-[#077f5d]" />
              )}
              <h2
                className="text-lg text-[#1a1a1a] uppercase"
                style={{ fontFamily: "'Press Start 2P', monospace", lineHeight: '1.4', fontSize: '12px' }}
              >
                {t('home.budget_composition.title')}
              </h2>
            </button>

            {/* Card Content */}
            {isKomposisiExpanded && (
              <div className="p-4">
                {budgetComposition.barChartData.length === 0 || totals.totalAllocated === 0 ? (
                  /* Empty State */
                  <div className="text-center py-8">
                    <p
                      className="text-[#077f5d] font-bold mb-4 text-sm"
                      style={{ fontFamily: "'Press Start 2P', monospace", lineHeight: '1.6' }}
                    >
                      {t('home.expenses.no_allocation_title')}
                    </p>
                    <TypewriterText
                      texts={[
                        t('home.expenses.no_allocation_text_1'),
                        `${t('home.expenses.no_allocation_text_2')}`
                      ]}
                      speed={35}
                      delayBetween={500}
                      showCursor={true}
                      className="text-gray-600 text-sm font-mono mb-6 px-4"
                      onFinish={() => console.log('Typing done')}
                      startOnView={false}
                    />
                    <div className="flex justify-center">
                      <button
                        onClick={() => navigate('/budget')}
                        className="px-6 py-3 bg-[#3cbe8c] text-white border-2 border-[#077f5d] font-bold uppercase"
                        style={{
                          fontFamily: "'Press Start 2P', monospace",
                          fontSize: '10px',
                          boxShadow: '4px 4px 0 #077f5d',
                          transition: 'all 0.1s'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.boxShadow = '2px 2px 0 #077f5d';
                          e.currentTarget.style.transform = 'translate(-2px, -2px)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.boxShadow = '4px 4px 0 #077f5d';
                          e.currentTarget.style.transform = 'translate(0, 0)';
                        }}
                      >
                        {t('home.expenses.set_budget')}
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    {/* Composite Bar Chart */}
                    <div className="mb-4">
                      <div className="flex h-6 border-2 border-[#077f5d] overflow-hidden">
                        {budgetComposition.barChartData.map((group) => (
                          <div
                            key={group.id}
                            className="flex items-center justify-center text-white font-bold font-mono text-xs"
                            style={{
                              width: `${group.percentage}%`,
                              backgroundColor: group.color,
                            }}
                            title={`${group.name}: ${group.percentage.toFixed(1)}%`}
                          >
                            {group.percentage >= 10 && `${group.percentage.toFixed(0)}%`}
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Top 5 + Lainnya Groups List */}
                    <div className="space-y-2 font-mono">
                      {budgetComposition.top5Groups.map((group, index) => (
                        <div key={group.id}>
                          {/* Group Row */}
                          <button
                            onClick={() => toggleGroup(group.id)}
                            className="w-full flex items-center justify-between hover:bg-gray-50 transition-colors"
                          >
                            <div className="flex items-center gap-2">
                              <span className="text-xs font-bold text-[#077f5d] w-6">#{index + 1}</span>
                              <div
                                className="w-3 h-3 border-2 border-[#077f5d]"
                                style={{ backgroundColor: group.color }}
                              ></div>
                              <span className="text-sm text-[#1a1a1a]">{group.name}</span>
                              {group.children.length > 0 && (
                                <>
                                  {expandedGroups.has(group.id) ? (
                                    <ChevronUp className="w-4 h-4 text-[#077f5d]" />
                                  ) : (
                                    <ChevronDown className="w-4 h-4 text-[#077f5d]" />
                                  )}
                                </>
                              )}
                            </div>
                            <span className="text-sm font-bold text-[#077f5d]">
                              {group.percentage.toFixed(1)}%
                            </span>
                          </button>

                          {/* Expanded Categories */}
                          {expandedGroups.has(group.id) && group.children.length > 0 && (
                            <div className="ml-8 mt-2 space-y-1 pb-2">
                              {group.children.map((child) => (
                                <div
                                  key={child.id}
                                  className="flex items-center justify-between text-xs"
                                >
                                  <span className="text-gray-600">{child.name}</span>
                                  <span className="text-gray-600">
                                    {child.percentageOfGroup.toFixed(1)}%
                                  </span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}

                      {/* Lainnya (Others) - 6th item */}
                      {budgetComposition.barChartData.length > 5 && budgetComposition.barChartData[5] && (
                        <div>
                          <div className="w-full flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <span className="text-xs font-bold text-[#077f5d] w-6">#6</span>
                              <div
                                className="w-3 h-3 border-2 border-[#077f5d]"
                                style={{ backgroundColor: budgetComposition.barChartData[5].color }}
                              ></div>
                              <span className="text-sm text-[#1a1a1a]">{budgetComposition.barChartData[5].name}</span>
                            </div>
                            <span className="text-sm font-bold text-[#077f5d]">
                              {budgetComposition.barChartData[5].percentage.toFixed(1)}%
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>

          {/* Spending Summary Card - Collapsible */}
          <div className="bg-white border-2 border-[#077f5d]" style={{ boxShadow: '4px 4px 0 #077f5d' }}>
            {/* Card Header - Collapsible */}
            <button
              onClick={() => setIsSpendingExpanded(!isSpendingExpanded)}
              className="w-full flex items-center justify-start gap-3 p-4 bg-[#a9edba] border-b-2 border-[#077f5d]"
            >
              {isSpendingExpanded ? (
                <ChevronUp className="w-5 h-5 text-[#077f5d]" />
              ) : (
                <ChevronDown className="w-5 h-5 text-[#077f5d]" />
              )}
              <h2
                className="text-lg text-[#1a1a1a] uppercase"
                style={{ fontFamily: "'Press Start 2P', monospace", lineHeight: '1.4', fontSize: '12px' }}
              >
                {t('home.trend.title')}
              </h2>
            </button>

            {/* Card Content */}
            {isSpendingExpanded && (
              <div className="p-4">
                {/* Loading State */}
                {transactionsLoading && (
                  <div className="flex items-center justify-center py-8" role="status" aria-live="polite">
                    <PixelHourglass size="medium" speed="fast" color="keppel" data-id="transactions-loader" />
                    <span className="sr-only">Loading transactions...</span>
                  </div>
                )}

                {/* Filters */}
                {!transactionsLoading && (
                  <>
                    <div className="mb-4">
                      {/* Category Filter - Hierarchical (same as HistoryPage) */}
                      <div>
                        <label className="block text-xs font-bold text-[#077f5d] mb-2 font-mono uppercase">
                          {t('home.trend.category')}
                        </label>
                        <select
                          value={selectedCategoryValue}
                          onChange={(e) => setSelectedCategoryValue(e.target.value)}
                          className="w-full px-3 py-2 text-sm font-mono border-2 border-[#077f5d] bg-white text-[#1a1a1a]"
                          style={{
                            boxShadow: '2px 2px 0 #077f5d',
                            transition: 'none'
                          }}
                        >
                          <option value="all">{t('home.trend.all_categories')}</option>
                          {categoryOptions.map((option, index) => (
                            <option
                              key={index}
                              value={option.value}
                              className={option.isParent ? 'font-semibold' : ''}
                              style={option.isParent ? { fontWeight: 'bold' } : {}}
                            >
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    {/* Line Chart (SVG-based) or Empty State */}
                    {spendingSummary.dailyData.length > 0 && spendingSummary.stats ? (() => {
                      const { totalAllocated } = spendingSummary.stats;
                      const chartHeight = 128; // 32 * 4 = h-32

                      // Use totalAllocated as the maximum height for proper scaling
                      const maxValue = Math.max(totalAllocated, 1);

                      const numPoints = spendingSummary.dailyData.length;

                      // Create SVG path for line chart - spread evenly across full width
                      // 1 point = center (50%), 2 points = left (0%) and right (100%), etc.
                      const points = spendingSummary.dailyData.map((data, index) => {
                        const x = numPoints === 1 ? 50 : (index / (numPoints - 1)) * 100;
                        const y = chartHeight - ((data.amount / maxValue) * chartHeight);
                        return `${x},${y}`;
                      }).join(' ');

                      // Calculate allocation line position
                      const allocationY = chartHeight - ((totalAllocated / maxValue) * chartHeight);

                      return (
                        <div
                          className="border-2 border-[#077f5d] p-4 pb-2 mb-2 bg-[#a9edba]"
                          style={{
                            boxShadow: '2px 2px 0 #077f5d',
                            transition: 'none'
                          }}
                        >
                          <div className="relative h-32">
                            <svg
                              viewBox={`0 0 100 ${chartHeight}`}
                              className="w-full h-full"
                              preserveAspectRatio="none"
                            >
                              {/* Transaction grid lines (vertical) - one for each transaction point */}
                              {spendingSummary.dailyData.map((data, index) => {
                                const x = numPoints === 1 ? 50 : (index / (numPoints - 1)) * 100;
                                return (
                                  <line
                                    key={`grid-${data.date}`}
                                    x1={x}
                                    y1="0"
                                    x2={x}
                                    y2={chartHeight}
                                    stroke="#3cbe8c"
                                    strokeWidth="0.5"
                                    opacity="1"
                                  />
                                );
                              })}

                              {/* Allocation line (dashed) */}
                              <line
                                x1="0"
                                y1={allocationY}
                                x2="100"
                                y2={allocationY}
                                stroke="#ff6b6b"
                                strokeWidth="4"
                                strokeDasharray="2,2"
                              />

                              {/* Spending line */}
                              <polyline
                                points={points}
                                fill="none"
                                stroke="#3cbe8c"
                                strokeWidth="1"
                                vectorEffect="non-scaling-stroke"
                              />

                              {/* Data points (squares) - positioned using fluid layout */}
                              {spendingSummary.dailyData.map((data, index) => {
                                const x = numPoints === 1 ? 50 : (index / (numPoints - 1)) * 100;
                                const y = chartHeight - ((data.amount / maxValue) * chartHeight);
                                const squareSize = 4;
                                return (
                                  <rect
                                    key={data.date}
                                    x={x - squareSize / 2}
                                    y={y - squareSize / 2}
                                    width={squareSize}
                                    height={squareSize}
                                    fill="#077f5d"
                                  >
                                    <title>{data.date}: {formatMoney(data.amount)}</title>
                                  </rect>
                                );
                              })}
                            </svg>
                          </div>
                          <div className="flex items-center justify-between mt-2 text-xs font-mono">
                            <span className="text-gray-600">
                              {spendingSummary.allDates[0] || ''}
                            </span>
                            <span className="text-gray-600">
                              {spendingSummary.allDates[spendingSummary.allDates.length - 1] || ''}
                            </span>
                          </div>
                          <div className="flex items-center gap-4 mt-2 text-xs font-mono">
                            <div className="flex items-center gap-1">
                              <div className="w-3 h-2 bg-[#077f5d]"></div>
                              <span className="text-gray-600">{t('home.trend.spending')}</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <div className="w-3 h-0.5 border-t-2 border-dashed border-[#ff6b6b]"></div>
                              <span className="text-gray-600">{t('home.trend.allocation')}</span>
                            </div>
                          </div>
                        </div>
                      );
                    })() : (
                      <div
                        className="border-2 border-[#077f5d] p-4 pb-2 mb-2 bg-[#a9edba]"
                        style={{
                          boxShadow: '2px 2px 0 #077f5d',
                          transition: 'none'
                        }}
                      >
                        <div className="relative h-32 flex items-center justify-center">
                          <p
                            className="text-[#077f5d] font-bold text-lg"
                            style={{ fontFamily: "'Press Start 2P', monospace" }}
                          >
                            [NO DATA]
                          </p>
                        </div>
                        <div className="flex items-center justify-between mt-2 text-xs font-mono">
                          <span className="text-gray-600"></span>
                          <span className="text-gray-600"></span>
                        </div>
                        <div className="flex items-center gap-4 mt-2 text-xs font-mono">
                          <div className="flex items-center gap-1">
                            <div className="w-3 h-2 bg-[#077f5d]"></div>
                            <span className="text-gray-600">{t('home.trend.spending')}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <div className="w-3 h-0.5 border-t-2 border-dashed border-[#ff6b6b]"></div>
                            <span className="text-gray-600">{t('home.trend.allocation')}</span>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Stats */}
                    {spendingSummary.stats ? (
                      <div className="space-y-3 font-mono">
                        <div className="flex items-center justify-between text-sm mt-4">
                          <span className="text-gray-600">{t('home.trend.total_days')}</span>
                          <span className="font-bold text-[#077f5d]">{spendingSummary.stats.totalDays}</span>
                        </div>
                        <div className="border-t-2 border-[#077f5d] pt-3 space-y-2">
                          <div className="flex items-center justify-between text-sm">
                            <p className="text-xs font-bold text-[#077f5d] uppercase">{t('home.trend.this_month')}</p>
                            <span className="font-bold text-[#077f5d]">{spendingSummary.stats.currentPercent}%</span>
                          </div>
                          <div className="flex items-center justify-end text-[10px]">
                            <span className="text-gray-500">
                              {formatMoney(spendingSummary.stats.totalSpent)} /
                              {formatMoney(spendingSummary.stats.totalAllocated)}
                            </span>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="text-center text-sm font-mono text-gray-500 py-4">
                        {t('home.trend.no_data')}
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        </Masonry>
      </div>

      {/* Add Goal Category Modal */}
      <SubCategoryModal
        isOpen={showAddGoalModal}
        onClose={() => setShowAddGoalModal(false)}
        onSave={handleSaveGoalCategory}
        title={t('home.goals.create_goal')}
        isGoalCreationMode={true}
        parentCategories={parentCategories}
      />
    </div>
  );
}
